<script setup lang="ts">
import { useProjectStore } from '@/stores/project'
import { useScenarioStore } from '@/stores/scenario'
import { storeToRefs } from 'pinia'
import {
  CreateOrUpdateDialog,
  DeleteDialog,
  DuplicateDialog,
  DataTableWrapper
} from '@haskoning/vue-component-library'
import { useAuthorizationStore } from '@/stores/authorization'
import { computed } from 'vue'
import type { Scenario } from '@/stores/type/scenario.type'
import ScenarioTableRow from '@/components/scenario-index/ScenarioTableRow.vue'
import { useArcgisLogin } from '@/components/project-detail/composable/arcgisLoginComposable'

const projectStore = useProjectStore()
const scenarioStore = useScenarioStore()
const authorizationStore = useAuthorizationStore()

const { showOrgArcgisAlert, login, reAuthenticateArcgisOnline } = useArcgisLogin()

const {
  currentItem: currentProjectItem,
  createUpdateDialog: projectCreateUpdateDialog,
  writeLoading: projectWriteLoading,
  fieldsFiltered: projectFieldsFiltered
} = storeToRefs(projectStore)
const {
  currentItem: currentScenarioItem,
  createUpdateDialog: scenarioCreateUpdateDialog,
  fieldsFiltered: scenarioFieldsFiltered,
  fetchLoading: scenarioFetchLoading,
  items: scenarioItems,
  selectedItems: scenarioSelectedItems,
  deleteDialog,
  duplicateDialog,
  deleteLoading,
  writeLoading: scenarioWriteLoading,
  canAddScenario,
  canChangeScenario,
  canDeleteScenario,
  isApprovedProjectParams
} = storeToRefs(scenarioStore)
const isCheckboxShown = computed(
  () => canAddScenario.value || canChangeScenario.value || canDeleteScenario.value
)

const headers = computed(() => [
  { title: '', align: isCheckboxShown.value ? 'left' : ' d-none' },
  { title: 'Simulation Name', align: 'left' },
  { title: 'Calculation Type', align: 'left' },
  { title: 'Origin Type', align: 'left' },
  { title: 'Destination type', align: 'left' },
  { title: 'Disruption type', align: 'left' },
  { title: 'Date Created', align: 'left' },
  { title: 'Favorite', align: 'center' },
  { title: 'Edit', align: isCheckboxShown.value ? 'center' : ' d-none' }
])

const filterItemsBySearch = <T extends Scenario>(
  items: T[],
  search: string,
  keys: (keyof T)[]
): T[] => {
  if (!search) return items
  return items.filter((item) =>
    keys.some((key) => item[key]?.toString().toLowerCase().includes(search.toLowerCase()))
  )
}

const getFilteredItems = (items: Scenario[], search: string, favorite: boolean): Scenario[] => {
  const filteredItems = filterItemsBySearch(items, search, [
    'name',
    'location',
    'client',
    'description'
  ])
    .sort((a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime())
    .sort((a, b) => Number(b.favorite) - Number(a.favorite))

  return favorite ? filteredItems.filter((item) => item.favorite) : filteredItems
}

const canChangeProject = computed(() => {
  return authorizationStore.hasServicePermission({
    authSource: 'ProjectUser',
    allowed: ['change_project']
  })
})

const handleEditProject = () => {
  projectStore.openCreateUpdateDialog()
}

const addScenario = () => {
  reAuthenticateArcgisOnline()
  if (currentProjectItem.value === null) return
  const newScenario = scenarioStore.createNewScenario(
    currentProjectItem.value.id,
    authorizationStore.arcgisToken
  )
  scenarioStore.setCurrentItem(newScenario)
  scenarioStore.openCreateUpdateDialog()
}

const canAddCalculationWithArcgis = computed(() => {
  return canAddScenario.value && !!authorizationStore.arcgisToken
})
const showArcgisWarning = computed(() => {
  return canAddScenario.value && canChangeScenario.value && !authorizationStore.arcgisToken
})

onMounted(() => {
  if (localStorage.getItem('arcgisToken')) {
    reAuthenticateArcgisOnline()
  }
})
</script>

<template>
  <template v-if="scenarioFetchLoading">
    <div class="d-flex justify-center align-center fill-height">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>
  </template>
  <template v-else>
    <div class="px-8 py-6">
      <DataTableWrapper
        v-if="isApprovedProjectParams || scenarioItems.length > 0"
        :title="currentProjectItem?.name || ''"
        add-title="Add Simulation"
        :headers="headers"
        :items="scenarioItems"
        :selected-items="scenarioSelectedItems"
        :get-filtered-items="getFilteredItems"
        :can-change-title="canChangeProject"
        :can-add-item="canAddCalculationWithArcgis"
        :can-change-item="canChangeScenario"
        :can-delete-item="canDeleteScenario"
        :show-view-favorites-button="true"
        :show-move-to-archive-button="false"
        :show-duplicate-button="true"
        :show-delete-button="true"
        @change-title="handleEditProject"
        @add-item="addScenario"
        @update-selected-items="scenarioStore.updateSelectedItems"
        @move-selected-items-to-archive="scenarioStore.moveSelectedItemsToArchive"
        @duplicate-selected-items="scenarioStore.duplicateSelectedItems"
        @delete-selected-items="scenarioStore.deleteSelectedItems"
      >
        <template #item="{ item }">
          <ScenarioTableRow :item="item as Scenario" :is-checkbox-shown="isCheckboxShown" />
        </template>
      </DataTableWrapper>

      <CreateOrUpdateDialog
        v-if="currentProjectItem"
        name="project"
        v-model="projectCreateUpdateDialog"
        v-model:item="currentProjectItem"
        :loading="projectWriteLoading"
        :fields="projectFieldsFiltered"
        @submit="projectStore.postOrUpdate($event)"
      />
      <CreateOrUpdateDialog
        v-if="currentScenarioItem"
        name="scenario"
        v-model="scenarioCreateUpdateDialog"
        v-model:item="currentScenarioItem"
        :loading="scenarioWriteLoading"
        :fields="scenarioFieldsFiltered"
        @submit="scenarioStore.postOrUpdate($event)"
      />
      <DeleteDialog
        name="scenario"
        v-model="deleteDialog"
        :loading="deleteLoading"
        :bulk-delete="true"
        v-model:items="scenarioSelectedItems"
        @bulk-delete="scenarioStore.bulkDelete($event)"
        @submitted-and-closed="scenarioStore.clearSelectedItems"
      />
      <DuplicateDialog
        name="scenario"
        v-model="duplicateDialog"
        :loading="scenarioWriteLoading"
        :bulk-duplicate="true"
        v-model:items="scenarioSelectedItems"
        @bulk-duplicate="scenarioStore.bulkDuplicate($event)"
        @submitted-and-closed="scenarioStore.clearSelectedItems"
      />
    </div>
    <v-dialog
      v-model="showOrgArcgisAlert"
      persistent
      width="480"
      transition="dialog-bottom-transition"
      :scrim="true"
    >
      <v-card
        class="d-flex flex-column align-center justify-center"
        style="padding: 32px 24px; background: #fff; border-radius: 12px"
        elevation="2"
      >
        <v-icon color="warning" size="40" class="mb-3">mdi-alert</v-icon>
        <span style="font-size: 1.1rem; font-weight: 500; text-align: center" class="mb-4">
          Please use an organizational ArcGIS Online account!
        </span>
        <div class="d-flex justify-space-between" style="width: 100%; gap: 16px">
          <v-btn color="primary" style="width: 48%; font-weight: 600" @click="login">
            ArcGIS Online Login
          </v-btn>
          <v-btn
            color="secondary"
            style="width: 48%; font-weight: 600"
            @click="showOrgArcgisAlert = false"
          >
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </template>
  <div v-if="showArcgisWarning && !scenarioFetchLoading" class="alert-below-title">
    <v-icon color="red" class="mr-2">mdi-alert-circle-outline</v-icon>
    <span>
      Please
      <a href="#" @click.prevent="login" style="color: #0097a9; text-decoration: underline">
        log in to ArcGIS
        <v-icon color="#0097a9" style="vertical-align: middle">mdi-open-in-new</v-icon>
      </a>
      to add new simulations and duplicate existing simulations.
    </span>
  </div>
</template>

<style scoped>
.alert-below-title {
  position: absolute;
  top: 60px;
  left: 30px;
  margin-top: 10px;
}
::v-deep(
  .v-card.undefinedtheme--haskoningLight.border-opacity-35.border-sm.v-card--density-default.rounded-lg.v-card--variant-text.mx-auto.mt-2
) {
  margin-top: 30px !important;
}
::v-deep(
  .v-card.v-theme--haskoningLight.border-opacity-35.border-sm.v-card--density-default.rounded-lg.v-card--variant-text.mx-auto.mt-2
) {
  margin-top: 25px !important;
}
</style>
